/**
 * ReceiveAllModal — batch-receive all unreceived lines on a PO.
 *
 * Shows remaining unreceived lines with pre-filled quantities.
 * User can adjust individual qtys (e.g., short shipment).
 * One click receives everything.
 */

import { useEffect, useState } from 'react';
import { receiveAllPoLines } from '../../../api/purchasing';
import { Button, Modal } from '../../common';

function fmtQty(val) {
  const n = parseFloat(val);
  if (isNaN(n)) return '0';
  return n % 1 === 0 ? String(Math.round(n)) : n.toFixed(2).replace(/\.?0+$/, '');
}

export default function ReceiveAllModal({ open, onClose, po, onReceived }) {
  const [lineQtys, setLineQtys] = useState({});   // lineId → qty string
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  // Build unreceived lines list from PO data
  const lines = (po?.lines || []).filter(
    l => l.line_status !== 'received' && l.line_status !== 'cancelled'
  );

  const unreceivedLines = lines.map(l => {
    const remaining = Math.max(parseFloat(l.qty_ordered || 0) - parseFloat(l.qty_received || 0), 0);
    return { ...l, remaining };
  }).filter(l => l.remaining > 0);

  // Pre-fill quantities when modal opens
  useEffect(() => {
    if (!open) return;
    const qtys = {};
    unreceivedLines.forEach(l => {
      qtys[l.id] = fmtQty(l.remaining);
    });
    setLineQtys(qtys);
    setNotes('');
    setResult(null);
    setError('');
  }, [open, po?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  function setQty(lineId, val) {
    setLineQtys(prev => ({ ...prev, [lineId]: val }));
  }

  async function handleReceiveAll() {
    const linesToReceive = unreceivedLines
      .filter(l => {
        const qty = parseFloat(lineQtys[l.id] || 0);
        return qty > 0;
      })
      .map(l => ({
        line_id: l.id,
        qty: parseFloat(lineQtys[l.id]),
      }));

    if (linesToReceive.length === 0) {
      setError('No quantities to receive.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const data = await receiveAllPoLines(po.id, linesToReceive, notes);
      setResult(data);
      onReceived?.();
      // Auto-close after brief delay if all successful
      if (data.error_count === 0) {
        setTimeout(() => onClose?.(), 1200);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Batch receive failed.');
    } finally {
      setSaving(false);
    }
  }

  if (!open || !po) return null;

  const totalToReceive = unreceivedLines.reduce((sum, l) => {
    const qty = parseFloat(lineQtys[l.id] || 0);
    return sum + (qty > 0 ? qty : 0);
  }, 0);

  return (
    <Modal open={open} onClose={onClose} title={`Receive All — PO ${po.po_number || '(draft)'}`} maxWidth="max-w-2xl">
      <div className="space-y-4">
        {/* Vendor info */}
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span className="font-medium text-gray-800">{po.vendor_name || 'Vendor'}</span>
          {po.po_number && <span className="text-gray-400">• PO {po.po_number}</span>}
        </div>

        {unreceivedLines.length === 0 ? (
          <div className="text-center py-8">
            <span className="text-3xl mb-2 block">✅</span>
            <p className="text-sm text-green-700 font-medium">All lines on this PO are fully received.</p>
          </div>
        ) : (
          <>
            {/* Lines table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                    <th className="pb-2 pr-3">Part #</th>
                    <th className="pb-2 pr-3">Description</th>
                    <th className="pb-2 pr-3 text-right">Ordered</th>
                    <th className="pb-2 pr-3 text-right">Received</th>
                    <th className="pb-2 pr-3 text-right">Remaining</th>
                    <th className="pb-2 text-right" style={{ width: 90 }}>Receive Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {unreceivedLines.map(line => {
                    const partNo = line.part_display || '—';
                    return (
                      <tr key={line.id} className="border-b border-gray-100">
                        <td className="py-2 pr-3 font-mono text-xs font-medium">{partNo}</td>
                        <td className="py-2 pr-3 text-xs text-gray-600 max-w-[200px] truncate">
                          {line.vendor_part ? `${line.vendor_name || ''} — ${line.vendor_part}` : ''}
                        </td>
                        <td className="py-2 pr-3 text-right">{fmtQty(line.qty_ordered)}</td>
                        <td className="py-2 pr-3 text-right">{fmtQty(line.qty_received)}</td>
                        <td className="py-2 pr-3 text-right text-red-600 font-medium">{fmtQty(line.remaining)}</td>
                        <td className="py-2 text-right">
                          <input
                            type="number"
                            min="0"
                            max={line.remaining}
                            step="any"
                            value={lineQtys[line.id] || ''}
                            onChange={e => {
                              let val = parseFloat(e.target.value);
                              if (val > line.remaining) val = line.remaining;
                              setQty(line.id, val > 0 ? String(val) : e.target.value);
                            }}
                            onFocus={e => e.target.select()}
                            disabled={saving}
                            className="w-20 border border-gray-300 rounded px-2 py-1 text-right text-sm focus:ring-1 focus:ring-blue-500"
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-xs text-gray-500 mb-1">Notes (optional — applies to all lines)</label>
              <input
                type="text"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="e.g. Packing slip #12345, received by Dylan"
                className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-1 focus:ring-blue-500"
                disabled={saving}
              />
            </div>

            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">{error}</div>
            )}

            {/* Result */}
            {result && (
              <div className={`rounded-md p-3 text-sm font-medium ${
                result.error_count > 0
                  ? 'bg-amber-50 border border-amber-200 text-amber-800'
                  : 'bg-green-50 border border-green-200 text-green-800'
              }`}>
                ✓ {result.received_count} line{result.received_count !== 1 ? 's' : ''} received
                {result.error_count > 0 && ` — ${result.error_count} error(s)`}
              </div>
            )}

            {/* Actions */}
            {!result && (
              <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                <p className="text-xs text-gray-400">
                  {totalToReceive > 0
                    ? `${fmtQty(totalToReceive)} total units to receive across ${unreceivedLines.filter(l => parseFloat(lineQtys[l.id] || 0) > 0).length} line(s)`
                    : 'Adjust quantities above'}
                </p>
                <div className="flex gap-2">
                  <Button variant="ghost" onClick={onClose}>Cancel</Button>
                  <Button
                    onClick={handleReceiveAll}
                    disabled={totalToReceive <= 0 || saving}
                    loading={saving}
                  >
                    {saving ? 'Receiving…' : `Receive All (${fmtQty(totalToReceive)} units)`}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
